import arrayMove from 'array-move';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { SortEndHandler } from 'react-sortable-hoc';

import { useMutation, useQuery } from '@apollo/client';
import { Grid } from '@material-ui/core';

import { DataRangeFilter } from 'components/DataRangeFilter/DataRangeFilter';
import DrawerNative from 'components/DrawerNative';
import Layout from 'components/Layout/Layout';
import Loading from 'components/Loading';
import PageError from 'components/PageError';
import PageTitle from 'components/PageTitle/PageTitle';
import ReplicateContentByTitle from 'components/ReplicateContentByTitle/ReplicateContentByTitle';
import {
  ISortColumnData,
  TableDragDrop,
} from 'components/TableDragDrop/TableDragDrop';
import { Toast } from 'components/Toast';
import AddTitles from 'pages/titlesCM/actions/create';
import UpdateTitles from 'pages/titlesCM/actions/edit';
import ImportUrls from 'pages/titlesCM/actions/importUrls';
import Replicator from 'pages/titlesCM/actions/replicator';
import {
  GET_TITLES_AS_CONTENTMANAGER,
  UPDATE_TITLE_PRIORITY,
} from 'queries/titleContent';
import {
  GET_ACCOUNT_MANAGERS,
  GET_REVIEWERS_WRITERS_TRANSLATORS,
} from 'queries/user';
import { useHistory, useLocation } from 'react-router-dom';
import { EQueryStringKey } from 'services/QueryString/QueryStringService';
import { UserService } from 'services/User/UserService';
import { usePermissions } from 'store/permissions.context';
import { queryParams } from 'utils/getQueryParams';

const TitlesContent: React.FC = () => {
  const [permissions, setPermissions] = usePermissions();
  const sites = UserService.useSitesFromAccess();
  const location = useLocation();
  const history = useHistory();

  const sortColumnData = useRef<ISortColumnData>({
    isActive: false,
    sortDirection: 'desc',
  });

  const [writers, setWriters] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [titles, setTitles] = useState<ITitle[]>([]);
  const [sitesFilter, setSitesFilter] = useState<string[]>(sites);
  const [isByPublishPost, setIsByPublishPost] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(
    moment().add(-6, 'days').startOf('day').toDate(),
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    moment().endOf('day').toDate(),
  );
  const [dateTypeFilter, setDateTypeFilter] = useState('Last7Days');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(10);
  const [filterLanguage, setFilterLanguage] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<number | null>(null);
  const [filterWriter, setFilterWriter] = useState(null);
  const [filterAccountManager, setFilterAccountManager] = useState(null);
  const [filterSearchText, setFilterSearchText] = useState('');
  const [searchText, setSearchText] = useState('');
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [openImportUrlsDrawer] = useState(false);
  const [openReplicatorDrawer] = useState(false);
  const [isUpdateDrawerOpen, setIsUpdateDrawerOpen] = useState(false);
  const [selectedTitleId, setSelectedTitleId] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [updateTitlePriority] = useMutation(UPDATE_TITLE_PRIORITY);
  const [openLoading, setOpenLoading] = useState(false);
  const [filterTag, setFilterTag] = useState(null);

  const [displayToolbar, setDisplayToolbar] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Map<string, ITitle>());

  const queryTitles = useQuery(GET_TITLES_AS_CONTENTMANAGER, {
    variables: {
      sites: sitesFilter,
      offset: page * limit,
      limit,
      dateStart: selectedStartDate,
      dateEnd: selectedEndDate,
      status: filterStatus,
      writer: filterWriter,
      accountManagerEmail: filterAccountManager,
      searchText: filterSearchText,
      tag: filterTag,
      byPublishPost: isByPublishPost,
      language: filterLanguage,
    },
    fetchPolicy: 'no-cache',
  });

  const { error } = queryTitles;

  const queryUsers = useQuery(GET_REVIEWERS_WRITERS_TRANSLATORS);
  const queryAccountManagers = useQuery(GET_ACCOUNT_MANAGERS);

  useEffect(() => {
    if (queryTitles.data) {
      const {
        data: { titlesAsContentManager },
      } = queryTitles;
      setTitles(titlesAsContentManager.titles);
      setTotal(titlesAsContentManager.total);
      setPermissions(titlesAsContentManager.permissions);
    }
  }, [queryTitles, setPermissions]);

  useEffect(() => {
    if (queryUsers.data) {
      const {
        data: { userListByRole },
      } = queryUsers;
      setWriters(userListByRole);
    }
  }, [queryUsers]);

  useEffect(() => {
    if (queryAccountManagers.data) {
      const {
        data: { userListByRole },
      } = queryAccountManagers;
      setAccountManagers(userListByRole);
    }
  }, [queryAccountManagers]);

  useEffect(() => {
    const params = queryParams();
    if (params.add_title && params.add_title === 'true') {
      setOpenAddDrawer(true);
    }
  }, []);

  const refetchTitles = async (showBackdrop = true) => {
    if (showBackdrop) setOpenLoading(true);
    const {
      data: {
        titlesAsContentManager: { total, titles },
      },
    } = await queryTitles.refetch!();

    if (showBackdrop) {
      setTitles(titles);
      setTotal(total);
      setOpenLoading(false);
      Toast('Titles updated successfully!');
    }
  };

  const onSortEnd: SortEndHandler = async ({ oldIndex, newIndex }) => {
    if (
      oldIndex === newIndex ||
      titles[oldIndex] === undefined ||
      titles[newIndex] === undefined
    ) {
      return;
    }

    const oldNode = titles[oldIndex];
    const newNode = titles[newIndex];
    const newTitles = arrayMove(titles, oldIndex, newIndex);
    setTitles(newTitles);
    const result = await updateTitlePriority({
      variables: {
        titleId: oldNode.id,
        titleIdReplaced: newNode.id,
      },
    });
    if (result && result.data && result.data.updateTitlePriority) {
      refetchTitles();
    }
  };

  const resetSortColumnData = () => {
    sortColumnData.current = {
      isActive: false,
      sortDirection: 'desc',
    };
  };

  const toggleSortColumnDirection = () => {
    if (sortColumnData.current.sortDirection === 'desc') {
      sortColumnData.current.sortDirection = 'asc';
    } else sortColumnData.current.sortDirection = 'desc';
  };

  const sortTitlesByColumn = (field: string) => {
    const clonedTitles = [...titles];

    sortColumnData.current.isActive = true;

    toggleSortColumnDirection();

    if (sortColumnData.current.sortDirection === 'asc') {
      clonedTitles.sort((a: any, b: any) => {
        return (a[field] || '').localeCompare(b[field] || '');
      });

      setTitles(clonedTitles);
    } else {
      clonedTitles.sort((a: any, b: any) => {
        return (b[field] || '').localeCompare(a[field] || '');
      });

      setTitles(clonedTitles);
    }
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setLimit(+event.target.value);

    setPage(0);
  };

  useEffect(() => {
    resetSortColumnData();
  }, [page]);

  const handleOpenUpdateTitleDrawer = (titleId: string) => {
    setSelectedTitleId(titleId);
    setIsUpdateDrawerOpen(true);

    const params = new URLSearchParams({ [EQueryStringKey.TitleId]: titleId });
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const handleCloseUpdateTitleDrawer = () => {
    setSelectedTitleId('');
    setIsUpdateDrawerOpen(false);

    const params = new URLSearchParams(location.search);
    params.delete(EQueryStringKey.TitleId);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  useEffect(() => {
    let mounted = true;

    const titleIdQueryString = new URLSearchParams(location.search).get(
      EQueryStringKey.TitleId,
    );

    if (mounted && titleIdQueryString && !isUpdateDrawerOpen) {
      handleOpenUpdateTitleDrawer(titleIdQueryString);
    }

    return () => (mounted = false as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <Loading openLoading={openLoading} />
      <Grid container spacing={0}>
        <Grid item xs={4}>
          <PageTitle title="Titles" />
          <br />
          <Grid item xs={8}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                columnGap: '12px',
                rowGap: '12px',
              }}
            >
              <DrawerNative
                title="Titles"
                labelButtonAction="Add"
                open={openAddDrawer}
              >
                <AddTitles refetch={refetchTitles} />
              </DrawerNative>

              <DrawerNative
                title="Import URLs"
                labelButtonAction="Import"
                open={openImportUrlsDrawer}
              >
                <ImportUrls />
              </DrawerNative>

              <DrawerNative
                title="Replicator"
                labelButtonAction="Replicator"
                open={openReplicatorDrawer}
              >
                <Replicator />
              </DrawerNative>

              {displayToolbar && (
                <ReplicateContentByTitle selectedItems={selectedItems} />
              )}
            </div>
          </Grid>
        </Grid>

        <DrawerNative
          title="Edit Titles"
          open={isUpdateDrawerOpen}
          onCloseDrawer={handleCloseUpdateTitleDrawer}
        >
          <UpdateTitles
            titleId={selectedTitleId}
            refetchTitles={refetchTitles}
            onCloseDrawer={handleCloseUpdateTitleDrawer}
          />
        </DrawerNative>

        <Grid item xs={8}>
          <DataRangeFilter
            showIsPublishPostFilter
            isByPublishPost={isByPublishPost}
            setIsByPublishPost={setIsByPublishPost}
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            dateTypeFilter={dateTypeFilter}
            setDateTypeFilter={setDateTypeFilter}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            writers={writers}
            accountManagers={accountManagers}
            filterWriter={filterWriter}
            setFilterWriter={setFilterWriter}
            filterAccountManager={filterAccountManager}
            setFilterAccountManager={setFilterAccountManager}
            filterSite={sitesFilter}
            setFilterSite={setSitesFilter}
            sites={sites}
            setPage={setPage}
            filterTag={filterTag}
            setFilterTag={setFilterTag}
            permissions={permissions}
            loading={queryTitles.loading}
            filterLanguage={filterLanguage}
            setFilterLanguage={setFilterLanguage}
          />
        </Grid>
      </Grid>
      {error ? (
        <PageError />
      ) : (
        <TableDragDrop
          items={titles}
          sortColumnData={sortColumnData.current}
          sortTitlesByColumn={sortTitlesByColumn}
          writers={writers}
          onSortEnd={onSortEnd}
          rowsPerPageOptions={[10, 25, 100]}
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          setFilterSearchText={setFilterSearchText}
          setSearchText={setSearchText}
          searchText={searchText}
          refreshTitles={refetchTitles}
          filterStatus={filterStatus}
          permissions={permissions}
          sitesList={sitesFilter}
          loading={queryTitles.loading}
          onOpenUpdateDrawer={handleOpenUpdateTitleDrawer}
          displayToolbar={displayToolbar}
          setDisplayToolbar={setDisplayToolbar}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
      )}
    </Layout>
  );
};

export default TitlesContent;
